import {format, isValid, parseISO} from 'date-fns'
import {utcToZonedTime, zonedTimeToUtc} from 'date-fns-tz'
import React, {ReactNode, useState, useEffect} from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {Icon, IconName} from '../../../components/library/icon/icon'
import styled, {css} from 'styled-components'
import {Select} from './select'
import {StyledLabel, StyledInlineLabel} from './text'

interface StyledDateInputProps {
  up: boolean
  readOnly?: boolean
}

const StyledDateInput = styled.div<StyledDateInputProps>`
  display: inline-flex;
  position: relative;
  width: 100%;

  label {
    display: block;
    position: absolute;
    z-index: 1;
    padding: 9px 10px 0 20px;
    top: 12px;
    left: 0;
    color: ${props => props.theme.grey50};
    transition: 0.1s top ease, 0.1s font-size ease;
  }
  input {
    z-index: 1;
    transition: all 0.1s ease;
    position: relative;
    border: none;
    display: block;
    padding: 28px 10px 10px 20px;
    color: transparent;
    transition: 0.1s opacity ease;
    width: 100%;
    max-width: none;
    font-size: ${props => props.theme.fontSize};
    line-height: ${props => props.theme.lineHeight};
    cursor: text;
    border-radius: ${p => p.theme.borderRadius};

    ${({readOnly, theme}) =>
      readOnly
        ? css`
            background-color: transparent;
            border: solid 1px ${theme.grey20};
          `
        : css`
            background-color: ${theme.grey05};
            &:focus-within,
            &:hover {
              background-color: ${theme.grey10};
            }
            &:active {
              background-color: ${theme.grey15};
            }
          `}

    &:focus {
      outline: none;
    }
    &::-webkit-inner-spin-button {
      display: none;
    }

    // No longer present in Chrome 83
    /* // svgs encoded with https://yoksel.github.io/url-encoder/ */
    &::-webkit-clear-button {
      transition: all 0.1s ease;
      -webkit-appearance: none;
      height: 40px;
      width: 40px;
      border-radius: ${p => p.theme.borderRadiusPill};
      padding: 0;
      margin-top: -20px;
      margin-right: 10px;
      background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14 6L6 14M14 14L6 6' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
      background-position: center;
      background-repeat: no-repeat;
      background-size: 20px 20px;
      cursor: pointer;
      &:hover,
      &:focus {
        background-color: ${p => p.theme.grey05};
      }
      &:active {
        background-color: ${p => p.theme.grey10};
      }
    }

    &::-webkit-calendar-picker-indicator {
      transition: all 0.1s ease;
      -webkit-appearance: none;
      height: 40px;
      width: 40px;
      border-radius: ${p => p.theme.borderRadiusPill};
      padding: 0;
      margin-top: -20px;
      color: transparent;
      background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7 3.5C7 2.94772 6.55228 2.5 6 2.5C5.44772 2.5 5 2.94772 5 3.5H7ZM15 3.5C15 2.94772 14.5523 2.5 14 2.5C13.4477 2.5 13 2.94772 13 3.5H15ZM5 6H15V4H5V6ZM15 6V16H17V6H15ZM15 16H5V18H15V16ZM5 16V6H3V16H5ZM4 9H16V7H4V9ZM7 5V3.5H5V5H7ZM15 5V3.5H13V5H15ZM5 16H5H3C3 17.1046 3.89543 18 5 18V16ZM15 16V18C16.1046 18 17 17.1046 17 16H15ZM15 6H17C17 4.89543 16.1046 4 15 4V6ZM5 4C3.89543 4 3 4.89543 3 6H5V6V4Z' fill='black'/%3E%3Ccircle cx='12.5' cy='13.5' r='1.5' fill='black'/%3E%3C/svg%3E%0A");
      background-position: center;
      background-repeat: no-repeat;
      background-size: 20px 20px;
      cursor: pointer;
      &:hover,
      &:focus {
        background-color: ${p => p.theme.grey05};
      }
      &:active {
        background-color: ${p => p.theme.grey10};
      }
    }
  }

  ${props =>
    props.up &&
    css`
      label {
        top: 0;
        font-size: ${p => p.theme.fontSizeS};
      }
      input {
        color: ${p => p.theme.black};
      }
    `}
`

interface DateInputProps {
  label?: string
  upLabel?: string
  value: string | Date // ISO 8601 when string
  onChange: (value: string) => void
  readOnly?: boolean
}

export interface DatePickerInputProps {
  label?: string
  upLabel?: string
  value?: string | Date | null // ISO 8601 when string
  onChange: (value: Date) => void // TODO this returns null | Date not Date https://linear.app/atomic-app/issue/PLA-5324/date-picker-onchange-is-typed-wrong
  iconLeft?: IconName
  iconRight?: IconName
  showTimeSelect?: boolean
  showTimeSelectOnly?: boolean
  timeIntervals?: number
  inline?: boolean
  readOnly?: boolean
  style?: React.CSSProperties
  showTzInfo?: boolean
  maxDate?: Date
}

export const DateInput = ({
  label,
  upLabel,
  value,
  onChange,
  readOnly
}: DateInputProps) => {
  const [isFocused, setIsFocused] = useState<boolean>(false)
  // TODO Note we have a bug here if the passed time is a string it's treated as a UTC date
  // it probably should instead be treated as local TZ date.
  const parsedValue =
    typeof value === 'string'
      ? value
        ? // assume value is ISO 8601, which starts with YYYY-MM-DD
          value.substring(0, 10)
        : ''
      : format(value, 'yyyy-MM-dd')

  return (
    <StyledDateInput
      up={!!isFocused || !!value || !!readOnly}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      readOnly={readOnly}
    >
      <label>
        {isFocused || value !== '' ? (upLabel ? upLabel : label) : label}
      </label>
      <input
        type="date"
        value={parsedValue}
        readOnly={readOnly}
        onChange={e => {
          // convert back to ISO 8061 when updating
          onChange(e.target.value ? new Date(e.target.value).toISOString() : '')
        }}
      />
    </StyledDateInput>
  )
}

const Primary = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  justify-content: center;
`

const iconShared = css`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledMediaLeft = styled.div`
  order: -1;
  z-index: 1;
  ${iconShared}
`

const StyledMediaRight = styled.div`
  order: 1;
  z-index: 1;
  ${iconShared}
`

const DatePickerContent = styled.div<{up: boolean}>`
  display: flex;
  & > .react-datepicker-wrapper {
    flex: 1;
  }
  .react-datepicker-popper {
    z-index: 5;
  }
  input {
    font-size: inherit;
    border: transparent;
    color: ${props => props.theme.black};
    border-radius: none;
    outline: none;
    background-color: inherit;
    transition: all 0.1s ease;
    line-height: ${props => props.theme.lineHeight};
    padding: 0;
  }
  > .tz-info {
    display: inline-flex;
    align-items: flex-end;
    font-size: ${props => props.theme.fontSizeS};
    line-height: ${props => props.theme.lineHeightS};
  }
`

const DatePickerWrap = styled.label<{inline?: boolean; readOnly?: boolean}>`
  box-sizing: border-box;
  font-family: atomic___Inter, -apple-system, BlinkMacSystemFont, Roboto, Arial,
    san-serif;
  font-size: ${props => props.theme.fontSize};
  ${props => (props.inline ? `min-height: 40px;` : `min-height: 60px;`)}
  width: 100%;
  display: flex;
  border-radius: 5px;
  transition: all 0.1s ease;
  cursor: text;
  color: ${props => props.theme.grey50};
  padding: 0px;
  &:focus-within,
  &:hover {
    background: ${props => props.theme.grey10};
  }
  max-height: 60px;

  svg {
    color: ${props => props.theme.grey100};
  }

  background: ${props => props.theme.grey05};
  &:focus-within,
  &:hover {
    background: ${props => props.theme.grey10};
  }
  &:active {
    background: ${props => props.theme.grey15};
  }

  ${p =>
    p.inline
      ? css`
          ${DatePickerContent} {
            padding: 10px 15px 10px 15px;
          }
          ${StyledMediaLeft} ~ div {
            margin-left: -40px;
            ${DatePickerContent} {
              padding: 10px 15px 10px 40px;
            }
          }
          ${StyledMediaRight} ~ div {
            margin-right: -40px;
            ${DatePickerContent} {
              padding: 10px 40px 10px 15px;
            }
          }
          ${StyledMediaLeft} + ${StyledMediaRight} ~ div {
            margin-right: 0;
            ${DatePickerContent} {
              padding: 10px 40px 10px 40px;
            }
          }
          ${StyledMediaLeft},
          ${StyledMediaRight} {
            width: 40px;
            height: 40px;
          }
        `
      : css`
          ${DatePickerContent} {
            padding: 30px 20px 10px 20px;
          }
          ${StyledMediaLeft} ~ div {
            margin-left: -60px;
            ${DatePickerContent} {
              padding: 30px 20px 10px 60px;
            }
          }
          ${StyledMediaRight} ~ div {
            margin-right: -60px;
            ${DatePickerContent} {
              padding: 30px 60px 10px 20px;
            }
          }
          ${StyledMediaLeft} + ${StyledMediaRight} ~ div {
            margin-right: 0;
            ${DatePickerContent} {
              padding: 30px 60px 10px 60px;
            }
          }
          ${StyledMediaLeft},
          ${StyledMediaRight} {
            width: 60px;
            height: 60px;
          }
        `};

  ${p =>
    p.readOnly &&
    css`
      border: 1px solid ${p.theme.grey20};
      background: ${p.theme.white};
      pointer-events: none;
      user-select: none;

      &:hover,
      &:focus,
      &:active {
        background: ${p.theme.white};
        border: 1px solid ${p.theme.grey20};
      }
    `}
`

const getIconLeft = (iconLeft: IconName) => {
  return (
    <StyledMediaLeft>
      <Icon name={iconLeft} />
    </StyledMediaLeft>
  )
}

const getIconRight = (iconRight: IconName) => {
  return (
    <StyledMediaRight>
      <Icon name={iconRight} />
    </StyledMediaRight>
  )
}

const getDateFormat = (isTime: boolean, isTimeOnly: boolean) => {
  if (isTime) {
    return isTimeOnly ? 'h:mm aa' : 'dd/MM/yyyy h:mm aa'
  } else {
    return 'dd/MM/yyyy'
  }
}

export const DatePickerInput = ({
  label,
  upLabel,
  value,
  onChange,
  iconLeft,
  iconRight,
  showTimeSelect,
  showTimeSelectOnly,
  timeIntervals,
  inline,
  readOnly,
  style,
  showTzInfo,
  maxDate
}: DatePickerInputProps) => {
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const date =
    typeof value === 'string' ? (value ? parseISO(value) : undefined) : value

  // isNaN() tells us if we have an Invalid Date
  const isValidDate = date && !isNaN(date.getTime())
  return (
    <DatePickerWrap
      style={style}
      readOnly={readOnly}
      inline={inline}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
    >
      {iconLeft ? getIconLeft(iconLeft) : null}
      {iconRight ? getIconRight(iconRight) : null}
      <Primary>
        <DatePickerContent up={!!isFocused || !!isValidDate}>
          {inline ? (
            <StyledInlineLabel>
              {!isFocused && !isValidDate && (upLabel ? upLabel : label)}
            </StyledInlineLabel>
          ) : (
            <StyledLabel up={!!isFocused || !!isValidDate} padding="20px 0">
              {isFocused || isValidDate ? (upLabel ? upLabel : label) : label}
            </StyledLabel>
          )}
          <DatePicker
            maxDate={maxDate}
            showTimeSelect={!!showTimeSelect}
            showTimeSelectOnly={!!showTimeSelectOnly}
            timeIntervals={timeIntervals}
            selected={date && isValid(date) ? date : undefined}
            dateFormat={getDateFormat(!!showTimeSelect, !!showTimeSelectOnly)}
            timeFormat="h:mm aa"
            onChange={onChange}
            todayButton="Today"
            showPopperArrow={false}
            calendarContainer={CalendarContainer}
            calendarClassName={getClassName(
              !!iconLeft,
              showTimeSelect,
              showTimeSelectOnly
            )}
            // auto mode sometimes goes "up" in modals and gets cut off.
            // positioning down means we'll always be able to scroll to the
            // popper
            popperPlacement="bottom-start"
          />
          {showTzInfo && (isFocused || value) ? (
            <div className="tz-info">
              {Intl.DateTimeFormat().resolvedOptions().timeZone}
            </div>
          ) : null}
        </DatePickerContent>
      </Primary>
    </DatePickerWrap>
  )
}

interface DatePickerTimeZoneInputProps
  extends Omit<DatePickerInputProps, 'onChange'> {
  value?: string | null // UTC timestamp
  offset?: string // UTC offset
  onChange: (utcDate: Date, offset: string) => void
}
const offsets = [
  {text: '-11:00', value: '-11:00'},
  {text: '-10:00', value: '-10:00'},
  {text: '-09:00', value: '-09:00'},
  {text: '-08:00', value: '-08:00'},
  {text: '-07:00', value: '-07:00'},
  {text: '-06:00', value: '-06:00'},
  {text: '-05:00', value: '-05:00'},
  {text: '-04:00', value: '-04:00'},
  {text: '-03:00', value: '-03:00'},
  {text: '-02:00', value: '-02:00'},
  {text: '-01:00', value: '-01:00'},
  {text: '+00:00', value: 'Z'},
  {text: '+01:00', value: '+01:00'},
  {text: '+02:00', value: '+02:00'},
  {text: '+03:00', value: '+03:00'},
  {text: '+04:00', value: '+04:00'},
  {text: '+05:00', value: '+05:00'},
  {text: '+06:00', value: '+06:00'},
  {text: '+07:00', value: '+07:00'},
  {text: '+08:00', value: '+08:00'},
  {text: '+09:00', value: '+09:00'},
  {text: '+10:00', value: '+10:00'},
  {text: '+11:00', value: '+11:00'},
  {text: '+12:00', value: '+12:00'},
  {text: '+13:00', value: '+13:00'}
]

export const DatePickerTimeZone = (props: DatePickerTimeZoneInputProps) => {
  const [offset, setOffset] = useState(props.offset || 'Z')
  useEffect(() => {
    setOffset(props.offset ?? 'Z')
  }, [props.offset])
  // Convert the UTC date string to the local zonedTime based on offset
  const zonedTime = utcToZonedTime(
    props?.value || '', // UTC time as a string
    offset // UTC Offset
  )
  return (
    <>
      <DatePickerInput
        {...props}
        style={{marginBottom: '20px'}}
        value={zonedTime}
        onChange={date => {
          const utcDate = zonedTimeToUtc(date, offset)
          return props.onChange(utcDate, offset)
        }}
        showTzInfo={false}
      />
      <Select
        label="At UTC offset"
        iconLeft={'globe'}
        value={offset}
        onChange={o => {
          if (o) {
            setOffset(o)
            const utcDate = zonedTimeToUtc(zonedTime, o)
            return props.onChange(utcDate, o)
          }
        }}
        options={offsets}
        readOnly={props.readOnly}
      />
    </>
  )
}

const getClassName = (
  iconLeft?: boolean,
  showTimeSelect?: boolean,
  showTimeSelectOnly?: boolean
) => {
  if (showTimeSelectOnly) {
    return iconLeft ? 'width-time-only icon-left' : 'width-time-only'
  } else if (showTimeSelect) {
    return iconLeft ? 'width-time icon-left' : 'width-time'
  } else {
    return iconLeft ? 'width-normal icon-left' : 'width-normal'
  }
}

const CalendarContainer = ({
  className,
  children
}: {
  className: string
  children: ReactNode[]
}): JSX.Element => {
  return <StyledCalendar className={className}>{children}</StyledCalendar>
}

const StyledCalendar = styled.div`
  top: 10px;
  border: none !important;
  box-shadow: ${p => p.theme.shadowDepth2};
  border-radius: ${p => p.theme.borderRadius};
  font-family: atomic___Inter !important;

  &.width-time {
    left: -20px;
    &.icon-left {
      left: 0px;
    }
    width: 340px;
    .react-datepicker__navigation--next {
      right: 120px;
    }
    .react-datepicker__month-container {
      width: 230px;
    }
    .react-datepicker__time {
      border-radius: 0 ${p => p.theme.borderRadius} ${p => p.theme.borderRadius}
        0;
      box-shadow: 1px 0 0 0 inset ${p => p.theme.grey10};
    }
    .react-datepicker__today-button {
      width: 230px;
    }
  }

  &.width-time-only {
    left: -10px;
    &.icon-left {
      left: 10px;
    }
    width: 100px;
    position: absolute;
    min-height: 300px;
  }

  &.width-normal {
    width: 250px;
    left: -20px;
    &.icon-left {
      left: 0px;
    }
    .react-datepicker__month-container {
      width: 100%;
    }
  }

  .react-datepicker__header {
    font-size: ${p => p.theme.fontSize};
    border-bottom: none;
    background: transparent;
    padding-top: 15px;
  }
  .react-datepicker__month-container {
    font-size: ${p => p.theme.fontSize};
  }
  .react-datepicker__month {
    margin: 0;
    padding: 5px 0;
  }
  .react-datepicker__navigation {
    margin-top: 5px;
  }
  .react-datepicker__day-names {
    font-size: ${p => p.theme.fontSizeXS};
    padding-top: 10px;
  }
  .react-datepicker__day-name {
    color: ${p => p.theme.grey50};
    width: 32px;
    margin: 0;
  }
  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background: ${p => p.theme.blue};
  }
  .react-datepicker__day--today {
    font-weight: normal;
    color: ${p => p.theme.blue};
    &.react-datepicker__day--selected,
    &.react-datepicker__day--keyboard-selected {
      color: ${p => p.theme.white};
    }
  }
  .react-datepicker__day {
    margin: 0;
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    outline: none;
    border-radius: ${p => p.theme.borderRadiusPill};
  }
  .react-datepicker__today-button {
    background: transparent;
    color: ${p => p.theme.blue};
    border-top: none;
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: ${p => p.theme.fontSizeS};
  }
  .react-datepicker__time-box {
    width: auto !important;
    height: 100%;
  }
  .react-datepicker__time-container {
    right: 0;
    min-width: 100px;
  }
  &.react-datepicker--time-only {
    min-width: 0;
    top: 0;
  }
  .react-datepicker__time-list {
    padding: 0;
    padding-top: 10px;
    /* height: auto !important;
    👆 this, it causes issues if scroll bars are enabled
      the downside is we get a time list that isn't quite as tall as the calendar
      but its a better tradeoff than the alternative
    */
  }
  .react-datepicker__time-container--with-today-button {
    border: none;
  }
  .react-datepicker__time-list-item {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 40px;
    font-size: ${p => p.theme.fontSize};
    padding: 0 20px !important;
    &.react-datepicker__time-list-item--selected {
      background: ${p => p.theme.blue} !important;
      font-weight: normal !important;
    }
  }
  .react-datepicker__header--time {
    display: none;
  }
  .react-datepicker__time {
    border: none;
    border-radius: ${p => p.theme.borderRadius};
    box-shadow: ${p => p.theme.shadowDepth2};
    height: 100%;
  }
`
